import { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';

export default function useUserAuth() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const startTime = performance.now();
    console.log('[useUserAuth] useEffect starting at:', startTime);

    const auth = getAuth();
    console.log('[useUserAuth] Setting up auth state listener');
    console.log('[useUserAuth] Auth instance:', {
      currentUser: auth.currentUser?.uid || 'null',
      config: auth.config,
      app: auth.app?.name || 'unknown',
      settings: auth.settings || 'no settings'
    });

    // Check if we're using emulator
    const isEmulator = auth.config?.emulator || window.location.hostname === 'localhost';
    console.log('[useUserAuth] Running in emulator mode:', isEmulator);

    // Increase timeout for slower networks/emulators, especially useful for emulator
    const timeoutId = setTimeout(() => {
      const timeoutElapsed = performance.now() - startTime;
      console.log('[useUserAuth] Auth initialization timeout triggered after:', timeoutElapsed + 'ms');
      console.log('[useUserAuth] Setting loading to false due to timeout');
      setIsLoading(false);
    }, 5000); // Increased to 5 second timeout

    let authStateChangeCount = 0;

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      authStateChangeCount++;
      const elapsed = performance.now() - startTime;

      console.log(`[useUserAuth] Auth state changed (${authStateChangeCount}) after ${elapsed}ms:`, {
        user: firebaseUser ? {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          isAnonymous: firebaseUser.isAnonymous,
          emailVerified: firebaseUser.emailVerified,
          photoURL: firebaseUser.photoURL,
          providerId: firebaseUser.providerId,
          providerData: firebaseUser.providerData.map(p => ({
            providerId: p.providerId,
            uid: p.uid,
            email: p.email
          })),
          metadata: {
            creationTime: firebaseUser.metadata.creationTime,
            lastSignInTime: firebaseUser.metadata.lastSignInTime
          }
        } : null,
        elapsed: elapsed + 'ms',
        changeNumber: authStateChangeCount
      });

      // Clear the timeout since auth state has been determined
      console.log('[useUserAuth] Clearing timeout as auth state determined');
      clearTimeout(timeoutId);

      console.log('[useUserAuth] Setting user state:', firebaseUser ? `uid: ${firebaseUser.uid}` : 'null');
      setUser(firebaseUser);

      console.log('[useUserAuth] Setting isLoading to false');
      setIsLoading(false);
    }, (error) => {
      // Auth state change error handler
      const elapsed = performance.now() - startTime;
      console.error('[useUserAuth] Auth state change error after', elapsed + 'ms:', {
        error: error.message,
        code: error.code,
        stack: error.stack
      });
      clearTimeout(timeoutId);
      setIsLoading(false);
    });

    console.log('[useUserAuth] Auth state listener set up, waiting for initial state...');

    return () => {
      const cleanupTime = performance.now() - startTime;
      console.log('[useUserAuth] Cleaning up auth state listener after', cleanupTime + 'ms');
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  const logout = async () => {
    try {
      console.log('[useUserAuth] Attempting to sign out user:', user?.uid || 'no user');
      const auth = getAuth();
      await signOut(auth);
      console.log('[useUserAuth] Sign out successful');
    } catch (error) {
      console.error('[useUserAuth] Error signing out:', {
        error: error.message,
        code: error.code,
        uid: user?.uid || 'no user'
      });
    }
  };

  const isSignedIn = user && !user.isAnonymous;
  const displayName = user?.displayName;
  const email = user?.email;

  return {
    user,
    isLoading,
    isSignedIn,
    displayName,
    email,
    logout
  };
}
